import { BoxOfferAutonomousPurchase } from "@modules/boxOffer/entities/boxOfferTypes"
import { word } from "@modules/core/utils/i18n"
import { HUText } from "@modules/ui/components/huText"
import Line from "@modules/ui/components/line"
import { useEffect, useRef, useState } from "react"
import { useTheme } from "styled-components"

export type HuAutonomousPurchaseFormProps = {
  setAutonomousPurchase: (b?: BoxOfferAutonomousPurchase) => void
  autonomousPurchase: BoxOfferAutonomousPurchase
} & HuAutonomousPurchaseProps

export type HuAutonomousPurchaseProps = {
  isPriceDisplay?: boolean
}

export const HuAutonomousPurchaseForm: React.FC<HuAutonomousPurchaseFormProps> = ({
  setAutonomousPurchase,
  autonomousPurchase,
  isPriceDisplay = false,
}) => {
  const theme = useTheme()
  const divRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [position, setPosition] = useState<number>(0)
  const [offerDisplay, setOfferDisplay] = useState({
    [BoxOfferAutonomousPurchase.NO_ONE]: true,
    [BoxOfferAutonomousPurchase.MEMBERS]: true,
    [BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN]: true,
    [BoxOfferAutonomousPurchase.PUBLIC]: true,
  })
  const [isHandleClick, setIsHandleClick] = useState(true)

  const handleResize = () => {
    if (divRef.current) {
      const { width, height } = (divRef.current as HTMLElement).getBoundingClientRect()
      setDimensions({ width, height })
    }
  }

  const switchAutonomousPurchase = (boxOfferAutonomousPurchase: BoxOfferAutonomousPurchase, forceNoOne?: boolean) => {
    const newOfferDisplay = {
      [BoxOfferAutonomousPurchase.NO_ONE]: forceNoOne ? false : true,
      [BoxOfferAutonomousPurchase.MEMBERS]: false,
      [BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN]: false,
      [BoxOfferAutonomousPurchase.PUBLIC]: false,
    }

    if (boxOfferAutonomousPurchase === BoxOfferAutonomousPurchase.MEMBERS) {
      setPosition(Math.floor(dimensions.width / 4 / 2) + Math.floor(dimensions.width / 4))
      newOfferDisplay[BoxOfferAutonomousPurchase.MEMBERS] = true
    } else if (boxOfferAutonomousPurchase === BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN) {
      setPosition(Math.floor(dimensions.width / 4 / 2) + Math.floor(dimensions.width / 4) * 2)
      newOfferDisplay[BoxOfferAutonomousPurchase.MEMBERS] = true
      newOfferDisplay[BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN] = true
    } else if (boxOfferAutonomousPurchase === BoxOfferAutonomousPurchase.PUBLIC) {
      setPosition(Math.floor(dimensions.width / 4 / 2) + Math.floor(dimensions.width / 4) * 3)
      newOfferDisplay[BoxOfferAutonomousPurchase.MEMBERS] = true
      newOfferDisplay[BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN] = true
      newOfferDisplay[BoxOfferAutonomousPurchase.PUBLIC] = true
    } else {
      setPosition(Math.floor(dimensions.width / 4 / 2))
    }

    setOfferDisplay(newOfferDisplay)
    setAutonomousPurchase(boxOfferAutonomousPurchase)
  }

  const handleClick = (b: BoxOfferAutonomousPurchase, forceNoOne?: boolean) => {
    if (!isHandleClick) return
    switchAutonomousPurchase(b, forceNoOne)
  }

  useEffect(() => {
    handleResize()
    window.addEventListener("resize", handleResize, false)
    if (!isPriceDisplay) switchAutonomousPurchase(BoxOfferAutonomousPurchase.NO_ONE, true)
    else switchAutonomousPurchase(autonomousPurchase)
    return () => {
      window.removeEventListener("resize", handleResize, false)
    }
  }, [])

  useEffect(() => {
    if (!isPriceDisplay) switchAutonomousPurchase(BoxOfferAutonomousPurchase.NO_ONE, true)
    else switchAutonomousPurchase(autonomousPurchase)
  }, [dimensions])

  useEffect(() => {
    if (!isPriceDisplay) {
      switchAutonomousPurchase(BoxOfferAutonomousPurchase.NO_ONE, true)
      setIsHandleClick(false)
    } else {
      // Force PUBLIC purchase until the other types are handled
      // switchAutonomousPurchase(autonomousPurchase)
      switchAutonomousPurchase(BoxOfferAutonomousPurchase.PUBLIC)
      setIsHandleClick(true)
    }
  }, [isPriceDisplay])

  // Only PUBLIC purchase is available until the other types are handled
  return (
    <div className="flex flex-column border-neutral-50 border-1 bg-neutral-800 p-4" style={{ borderRadius: "20px" }}>
      <HUText fontStyle="BM" color={theme.mainWhite}>
        {word("autonomousPurchase.form.title")}
      </HUText>
      <Line color={`${theme.neutralColor300}`} strokeWidth={1} />
      {/* TITLE */}
      <div className={`flex flex-row gap-4`}>
        <HUText
          fontStyle="TS"
          // color={offerDisplay[BoxOfferAutonomousPurchase.NO_ONE] ? theme.mainWhite : theme.neutralColor400}
          color={theme.neutralColor400}
          className={`w-3 transition-duration-200 ${/*isHandleClick ? " cursor-pointer" :*/ ""}`}
          // onClick={() => handleClick(BoxOfferAutonomousPurchase.NO_ONE)}
        >
          {word("autonomousPurchase.form.noOne.title")}
        </HUText>
        <HUText
          fontStyle="TS"
          // color={offerDisplay[BoxOfferAutonomousPurchase.MEMBERS] ? theme.mainWhite : theme.neutralColor400}
          color={theme.neutralColor400}
          className={`w-3 transition-duration-200 ${/*isHandleClick ? " cursor-pointer" :*/ ""}`}
          // onClick={() => handleClick(BoxOfferAutonomousPurchase.MEMBERS)}
        >
          {word("autonomousPurchase.form.members.title")}
        </HUText>
        <HUText
          fontStyle="TS"
          // color={offerDisplay[BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN] ? theme.mainWhite : theme.neutralColor400}
          color={theme.neutralColor400}
          className={`w-3 transition-duration-200 ${/*isHandleClick ? " cursor-pointer" :*/ ""}`}
          // onClick={() => handleClick(BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN)}
        >
          {word("autonomousPurchase.form.leadsAndDropIn.title")}
        </HUText>
        <HUText
          fontStyle="TS"
          color={offerDisplay[BoxOfferAutonomousPurchase.PUBLIC] ? theme.mainWhite : theme.neutralColor400}
          className={`w-3 transition-duration-200 ${isHandleClick ? " cursor-pointer" : ""}`}
          onClick={() => handleClick(BoxOfferAutonomousPurchase.PUBLIC)}
        >
          {word("autonomousPurchase.form.public.title")}
        </HUText>
      </div>

      {/* DESCRIPTION */}
      <div className={`flex flex-row mt-2 gap-3`}>
        <HUText
          fontStyle="BS"
          // color={offerDisplay[BoxOfferAutonomousPurchase.NO_ONE] ? theme.mainWhite : theme.neutralColor400}
          color={theme.neutralColor400}
          className={`w-3 transition-duration-200
            ${/*isHandleClick ? " cursor-pointer" : */ ""}`}
          // onClick={() => handleClick(BoxOfferAutonomousPurchase.NO_ONE)}
        >
          {word("autonomousPurchase.form.noOne.subtitle")}
          <HUText fontStyle="BXS" color={theme.neutralColor400} className="block font-italic mt-2">
            {word("menu.coming_soon")}
          </HUText>
        </HUText>
        <HUText
          fontStyle="BS"
          // color={offerDisplay[BoxOfferAutonomousPurchase.MEMBERS] ? theme.mainWhite : theme.neutralColor400}
          color={theme.neutralColor400}
          className={`w-3 transition-duration-200 ${/*isHandleClick ? " cursor-pointer" :*/ ""}`}
          // onClick={() => handleClick(BoxOfferAutonomousPurchase.MEMBERS)}
        >
          {word("autonomousPurchase.form.members.subtitle")}
          <HUText fontStyle="BXS" color={theme.neutralColor400} className="block font-italic mt-2">
            {word("menu.coming_soon")}
          </HUText>
        </HUText>
        <HUText
          fontStyle="BS"
          // color={offerDisplay[BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN] ? theme.mainWhite : theme.neutralColor400}
          color={theme.neutralColor400}
          className={`w-3 transition-duration-200 ${/*isHandleClick ? " cursor-pointer" : */ ""}`}
          // onClick={() => handleClick(BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN)}
        >
          {word("autonomousPurchase.form.leadsAndDropIn.subtitle")}
          <HUText fontStyle="BXS" color={theme.neutralColor400} className="block font-italic mt-2">
            {word("menu.coming_soon")}
          </HUText>
        </HUText>
        <HUText
          fontStyle="BS"
          color={offerDisplay[BoxOfferAutonomousPurchase.PUBLIC] ? theme.mainWhite : theme.neutralColor400}
          className={`w-3 transition-duration-200 ${isHandleClick ? " cursor-pointer" : ""}`}
          onClick={() => handleClick(BoxOfferAutonomousPurchase.PUBLIC)}
        >
          {word("autonomousPurchase.form.public.subtitle")}
        </HUText>
      </div>

      {/* COLORS */}
      <div ref={divRef} className={`flex flex-row align-items-center w-full mt-3 relative h-3rem`}>
        <div
          className={`flex w-3 h-1rem border-round-left-lg transition-duration-400 ${
            // (offerDisplay[BoxOfferAutonomousPurchase.NO_ONE] ? "bg-electricPurple-200" : "bg-neutralColor-700") + (isHandleClick ? " cursor-pointer":"")
            "bg-neutralColor-300"
          }`}
          // onClick={() => handleClick(BoxOfferAutonomousPurchase.NO_ONE)}
        ></div>
        <div
          className={`flex w-3 h-1rem transition-duration-400 ${
            // (offerDisplay[BoxOfferAutonomousPurchase.MEMBERS] ? "bg-electricPurple-300" : "bg-neutralColor-700")+ (isHandleClick ? " cursor-pointer":"")
            "bg-neutralColor-400"
          }`}
          // onClick={() => handleClick(BoxOfferAutonomousPurchase.MEMBERS)}
        ></div>
        <div
          className={`flex w-3 h-1rem transition-duration-400 ${
            // (offerDisplay[BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN] ? "bg-electricPurple-400" : "bg-neutralColor-700")+ (isHandleClick ? " cursor-pointer":"")
            "bg-neutralColor-500"
          }`}
          // onClick={() => handleClick(BoxOfferAutonomousPurchase.LEADS_AND_DROP_IN)}
        ></div>
        <div
          className={`flex w-3 h-1rem border-round-right-lg transition-duration-400 ${
            (offerDisplay[BoxOfferAutonomousPurchase.PUBLIC] ? "bg-electricPurple-500" : "bg-neutralColor-700") +
            (isHandleClick ? " cursor-pointer" : "")
          }`}
          onClick={() => handleClick(BoxOfferAutonomousPurchase.PUBLIC)}
        ></div>
        <div
          className="absolute rounded-33 bg-neutral-700 h-full transition-duration-400"
          style={{ left: `${position.toString()}px`, width: "10px" }}
        ></div>
      </div>
    </div>
  )
}
